/**
 * The arena's builds as fighters: the stats a build fights with (for its body in the ring) and a
 * rough strength figure to show beside a name.
 */
import { skillPowerMult } from './cultivation';
import type { ArenaBuild } from './data/arena';
import { equipmentStats, type ItemRoll } from './data/items';
import { getArt, getSkill } from './data/skills';
import { attributesFor, deriveStats } from './stats';

export interface ArenaStats {
  maxHp: number;
  /** Striking power and defence for the art's damage type (external or internal). */
  atk: number;
  def: number;
  hit: number;
  dodge: number;
  crit: number;
  lifesteal: number;
  /** Who acts more often. */
  speed: number;
  /** Damage multiplier from the art's move levels. */
  mult: number;
  skills: string[];
}

export function arenaStats(b: ArenaBuild, rolls: Readonly<Record<string, ItemRoll>> = {}): ArenaStats {
  const d = deriveStats(attributesFor(b.neigong, b.neigongLevel));
  const g = equipmentStats(b.equipment, rolls);
  const art = getArt(b.art);
  const inner = art.damage === 'internal';
  return {
    maxHp: d.maxHp + g.maxHp,
    atk: (inner ? d.magic : d.atk) + g.atk,
    def: (inner ? d.magicDef : d.def) + g.def,
    hit: inner ? d.magicHit : d.hit,
    dodge: d.dodge,
    crit: Math.min(0.6, (inner ? d.magicCrit : d.crit) + g.crit),
    lifesteal: g.lifesteal,
    speed: d.speed * (1 + g.speed),
    mult: skillPowerMult(b.skillLevel) * (1 + (g.arts[b.art] ?? 0)),
    skills: art.skills.filter((id) => ['attack', 'break', 'power'].includes(getSkill(id).kind)),
  };
}

/** A rough strength figure to show beside a name. */
export const arenaPower = (s: ArenaStats): number =>
  Math.round(s.maxHp * 0.6 + s.atk * s.mult * 12 + s.def * 4 + s.crit * 400 + s.dodge * 2);

/** A build's fighting stats, to give the body that steps into the ring. */
export function arenaBody(b: ArenaBuild, rolls: Readonly<Record<string, ItemRoll>> = {}) {
  const d = deriveStats(attributesFor(b.neigong, b.neigongLevel));
  const g = equipmentStats(b.equipment, rolls);
  // Move levels make every strike harder, as they do for the player's own moves.
  const m = skillPowerMult(b.skillLevel) * (1 + (g.arts[b.art] ?? 0));
  return {
    maxHp: d.maxHp + g.maxHp,
    hp: d.maxHp + g.maxHp,
    maxQi: d.maxQi,
    qi: d.maxQi,
    atk: (d.atk + g.atk) * m,
    def: d.def + g.def,
    magic: (d.magic + g.atk) * m,
    magicDef: d.magicDef,
    hit: d.hit,
    magicHit: d.magicHit,
    dodge: d.dodge,
    crit: Math.min(0.6, d.crit + g.crit),
    magicCrit: Math.min(0.6, d.magicCrit + g.crit),
    toughness: d.toughness,
    hpRegen: d.hpRegen,
    maxStamina: d.maxStamina,
    stamina: d.maxStamina,
    speed: d.speed * (1 + g.speed),
  };
}
